import React, { useState, useRef } from 'react';
import './MessageInput.scss';
import fileIcon from '../images/file.png';
import sendIcon from '../images/send.png';
import emojiIcon from '../images/emoji.png';

const MessageInput = ({ onSendMessage, onTyping }) => {
  const [message, setMessage] = useState('');
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // 자주 사용하는 이모지들
  const quickEmojis = [
    '😀', '😂', '🥰', '😢', '😮', '😡', '🤔', '😍', '🥺', '😭', '😎', '🤩',
    '👍', '👎', '👏', '🙌', '🤝', '🙏', '✌️', '👌', '👋', '🤚',
    '❤️', '💙', '💚', '💛', '🧡', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕'
  ];

  // 메시지 전송
  const handleSendMessage = (e) => {
    e.preventDefault();
    
    if (message.trim()) {
      onSendMessage(message.trim(), 'text');
      setMessage('');
      setIsEmojiPickerOpen(false);
      textareaRef.current?.focus();
    }
  };

  // 텍스트 영역 키 다운 핸들러
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Shift + Enter: 줄바꿈
        return;
      } else {
        // Enter: 메시지 전송
        e.preventDefault();
        handleSendMessage(e);
      }
    }
  };

  // 텍스트 변경
  const handleTextChange = (e) => {
    setMessage(e.target.value);
    
    // 타이핑 중임을 알리는 함수가 있다면 호출
    if (onTyping && typeof onTyping === 'function') {
      onTyping();
    }
  };

  // 이모지 선택
  const handleEmojiSelect = (emoji) => {
    setMessage(prev => prev + emoji);
    setIsEmojiPickerOpen(false);
    textareaRef.current?.focus();
  };

  // 클립보드 붙여넣기 처리
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // 이미지 파일인 경우
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault(); // 기본 붙여넣기 동작 방지
        
        const file = item.getAsFile();
        if (file) {
          await uploadImageFile(file);
        }
        return;
      }
    }
  };

  // 이미지 파일 업로드 (클립보드와 파일 선택에서 공통 사용)
  const uploadImageFile = async (file) => {
    setIsFileUploading(true);
    
    try {
      // Firebase Storage에 이미지 업로드
      const { ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
      const { storage } = await import('../firebaseConfig');
      
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substr(2, 9);
      const fileName = `images/${timestamp}_${randomId}_${file.name || 'clipboard_image.png'}`;
      const imageRef = storageRef(storage, fileName);
      
      console.log('이미지 업로드 시작:', fileName);
      
      // 이미지 업로드
      const uploadTask = uploadBytesResumable(imageRef, file);
      
      const downloadURL = await new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log(`이미지 업로드 진행률: ${progress}%`);
          },
          (error) => {
            console.error('이미지 업로드 실패:', error);
            reject(error);
          },
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          }
        );
      });

      // 이미지 데이터 구성
      const imageData = [{
        url: downloadURL,
        name: file.name || 'clipboard_image.png',
        size: file.size,
        type: 'image'
      }];

      // 이미지 메시지로 바로 전송
      onSendMessage('', 'image', imageData);
      console.log('이미지 업로드 및 전송 완료:', imageData);
      
    } catch (error) {
      console.error('이미지 업로드 실패:', error);
      alert('이미지 업로드에 실패했습니다: ' + error.message);
    } finally {
      setIsFileUploading(false);
    }
  };

  // 파일 선택
  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  // 파일 업로드 처리 (일반 파일과 이미지 통합)
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 이미지 파일인 경우 공통 함수 사용
    if (file.type.startsWith('image/')) {
      await uploadImageFile(file);
      e.target.value = ''; // 파일 입력 초기화
      return;
    }

    setIsFileUploading(true);
    
    try {
      // Firebase Storage에 파일 업로드
      const { ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
      const { storage } = await import('../firebaseConfig');
      
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substr(2, 9);
      const fileName = `files/${timestamp}_${randomId}_${file.name}`;
      const fileRef = storageRef(storage, fileName);
      
      console.log('파일 업로드 시작:', fileName);
      
      // 파일 업로드
      const uploadTask = uploadBytesResumable(fileRef, file);
      
      const downloadURL = await new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log(`업로드 진행률: ${progress}%`);
          },
          (error) => {
            console.error('파일 업로드 실패:', error);
            reject(error);
          },
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          }
        );
      });

      // 파일 메시지 전송
      const fileData = {
        url: downloadURL,
        name: file.name,
        size: file.size,
        type: file.type || 'unknown'
      };

      onSendMessage(file.name, 'file', fileData);
      console.log('파일 업로드 완료:', fileData);
      
    } catch (error) {
      console.error('파일 업로드 실패:', error);
      alert('파일 업로드에 실패했습니다: ' + error.message);
    } finally {
      setIsFileUploading(false);
      e.target.value = ''; // 파일 입력 초기화
    }
  };

  return (
    <div className="message-input-container">
      {/* 이모지 피커 */}
      {isEmojiPickerOpen && (
        <div className="emoji-picker">
          <div className="emoji-grid">
            {quickEmojis.map((emoji, index) => (
              <button
                key={index}
                className="emoji-item"
                onClick={() => handleEmojiSelect(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>
          <button 
            className="close-emoji-picker"
            onClick={() => setIsEmojiPickerOpen(false)}
          >
            ✕
          </button>
        </div>
      )}

      <form onSubmit={handleSendMessage} className="message-input-form">
        <div className="input-field-wrapper">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder="메시지 입력..."
            className="message-input"
            rows={1}
            disabled={isFileUploading}
            autoFocus
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
        </div>

        <div className="input-actions-bottom">
          <div className="input-actions-left">
            <button
              type="button"
              className="action-btn file-btn"
              onClick={handleFileSelect}
              disabled={isFileUploading}
              title="파일 업로드"
            >
              {isFileUploading ? '⏳' : <img src={fileIcon} alt="file" className="btn-icon" />}
            </button>
            
            <button
              type="button"
              className="action-btn emoji-btn"
              onClick={() => setIsEmojiPickerOpen(!isEmojiPickerOpen)}
              title="이모지"
            >
              <img src={emojiIcon} alt="emoji" className="btn-icon" />
            </button>
          </div>

          <button
            type="submit"
            className={`send-btn ${message.trim() ? 'active' : ''}`}
            disabled={!message.trim() || isFileUploading}
            title="전송"
          >
            <img src={sendIcon} alt="send" className="btn-icon" />
          </button>
        </div>
      </form>

      {/* 숨겨진 파일 입력 - 모든 파일 타입 허용 */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept="*"
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default MessageInput;